import React from 'react'
import { graphql, useFragment } from 'react-relay'
import { Collapse, Divider, Typography } from '@mui/material'
import Condition from 'components/Condition'
import Tags from 'components/Tags'
import { Summary_UserProfile$key } from '__generated__/Summary_UserProfile.graphql'
import { useTheme } from '@mui/material/styles'
import useStyles from './styles'
import NameFields from '../fields/NameFields'
import FreelancerTypeField from '../fields/IndustryField'
import TextIntroductionField from '../fields/TextIntroductionField'
import AvatarField from '../fields/AvatarField'
import LocationFields from '../fields/LocationFields'
import ProfileRateFields from '../fields/ProfileRateFields'
import FreelancerSubtypesFields from '../fields/SpecializationsFields'
import SocialLinksFields from '../fields/SocialLinksFields'
import BadgesArea from './components/BadgesArea'

export interface ISummaryProps {
  user: Summary_UserProfile$key
  editable: boolean
}

function Summary(props: ISummaryProps) {
  const { classes } = useStyles()
  const { user: userFragmentRef, editable } = props
  const theme = useTheme()

  const user = useFragment(graphql`
    fragment Summary_UserProfile on User {
      profile {
        openToOpportunities
      }

      ...SpecializationsFields_User
      ...TextIntroductionField_User
      ...LocationFields_User
      ...AvatarField_User
      ...NameFields_User
      ...IndustryField_User
      ...SocialLinksFields_User
      ...BadgesArea_User
      ...ProfileRateFields_User
    }
  `, userFragmentRef)

  const liteMode = !user?.profile?.openToOpportunities

  return (
    <div className={classes.summary} data-cy="summary">
      <div className={classes.mapWrapper}>
        <LocationFields
          editable={editable}
          user={user}
        />
      </div>

      <div className={classes.headline}>
        <AvatarField
          user={user}
          editable={editable}
        />

        <div className={classes.name} data-cy="profile-summary" style={{ marginBottom: editable ? theme.spacing(3) : undefined }}>
          <Condition condition={editable}>
            <NameFields
              user={user}
              editable={editable}
            />
            <FreelancerTypeField
              user={user}
              editable={editable}
            />
          </Condition>

          <Condition condition={!editable}>
            <Tags>
              <NameFields
                user={user}
                editable={editable}
              />
              <FreelancerTypeField
                user={user}
                editable={editable}
              />
            </Tags>
          </Condition>
        </div>

        {/* TODO: move hooks up so that we can mount/unmount section on enter/exit */}
        <Collapse in={!liteMode}>
          {editable && (
            <Divider className={classes.divider} />
          )}

          <BadgesArea
            editable={editable}
            user={user}
          />

          <div className={classes.actions}>
            <SocialLinksFields editable={editable} user={user} />
          </div>

          <div className={editable ? classes.editableRateFields : undefined}>
            <ProfileRateFields editable={editable} user={user} />
          </div>

          <Divider className={classes.divider} />

          <div className={classes.specializations}>
            <FreelancerSubtypesFields user={user} editable={editable} />
          </div>

          {editable && (
            <Divider className={classes.divider} />
          )}

          <div className={classes.introduction} data-cy="text-introduction">
            <Typography variant="body1">
              <TextIntroductionField
                user={user}
                editable={editable}
              />
            </Typography>
          </div>
        </Collapse>

        <Collapse mountOnEnter unmountOnExit in={liteMode}>
          <div
            // spacer
            style={{ height: 48 }}
          />
        </Collapse>
      </div>
    </div>
  )
}

export default Summary
